import { BookingDateSummaryInstance } from './formatBookings';
import { ProfileReservation } from './getReservationActions';

/** One booking: a single reservation, or every loaded instance of a series. */
export type ProfileBookingGroup = {
  /**
   * The instance the card represents and its actions act on — the earliest one
   * in the list, matching the legacy profile page, which acted on
   * `instances[0]` of a grouped series.
   */
  reservation: ProfileReservation;
  /** The booking's instances, in the order they were given. */
  instances: ProfileReservation[];
  /**
   * The instances the card's date line is built from, when the booking is a
   * series and its full remaining run is known (it may reach past whatever
   * window `instances` came from). Absent until that lookup lands; callers
   * fall back to `instances`.
   */
  summaryInstances?: BookingDateSummaryInstance[];
};

/**
 * Collapses reservations into one entry per booking.
 *
 * A multi-day booking comes back from the API as one reservation per day
 * sharing a `seriesId`, which would otherwise render as one card per day. The
 * legacy profile page grouped these on `series_id` and showed a single card
 * summarizing the span; this is the same grouping.
 *
 * Reservations are expected to already be sorted by start time, which makes
 * the first instance of each group the earliest one and orders the groups by
 * when each booking starts.
 */
export const groupBookingsBySeries = (
  reservations: ProfileReservation[]
): ProfileBookingGroup[] => {
  const groups = new Map<string, ProfileBookingGroup>();

  reservations.forEach((reservation) => {
    const key = reservation.seriesId ?? reservation.id;
    const group = groups.get(key);
    if (group) {
      group.instances.push(reservation);
      return;
    }
    groups.set(key, { reservation, instances: [reservation] });
  });

  return [...groups.values()];
};
